/**
 * ZEN ERP — Yazdırma Merkezi PDF Önizleme
 *
 * Chrome'un yerleşik PDF görüntüleyicisi (<object> — koyu kutu, İngilizce araç
 * çubuğu, UUID başlık) yerine pdf.js ile kendi önizlememiz:
 *   • Beyaz sayfa, degrade zemin üzerinde ORTALANIR ve ekrana SIĞDIRILIR
 *   • Türkçe araç çubuğu: − %100 + · Sayfaya sığdır · PDF İndir · Yazdır
 *   • Düğmeler program standardı zen-evrak-btn geometrisindedir
 *   • Çok sayfalı belgeler (ör. etiket PDF'leri) dikey akışta gösterilir
 *
 * Tulfon evrakındaki PdfDocumentPreview ile aynı pdf.js yaklaşımı kullanılır.
 */
import React, { useEffect, useRef, useState } from "react";
import { FileDown, Loader2, Maximize2, Printer, ZoomIn, ZoomOut } from "lucide-react";
import { loadPdfDocument } from "../utils/pdfWorker";

const MIN_ZOOM = 0.25;
const MAX_ZOOM = 4;
const ZOOM_STEP = 1.2;

// ── Türkçe önizleme araç çubuğu — zen-evrak-btn geometrisi ────────────────────
export function PreviewToolbar({
  zoomPct, fitActive = false, busy = false, fitLabel = "Sayfaya sığdır",
  onZoomIn, onZoomOut, onFit, onDownload, onPrint,
}) {
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="preview-toolbar">
      <div className="flex items-center gap-2">
        <button type="button" data-testid="preview-zoom-out" aria-label="Uzaklaştır" title="Uzaklaştır"
          onClick={onZoomOut} disabled={busy} className="zen-evrak-btn">
          <ZoomOut className="w-4 h-4" />
        </button>
        <span data-testid="preview-zoom-level"
          className="text-xs font-semibold tabular-nums min-w-[3.5rem] text-center select-none">%{zoomPct}</span>
        <button type="button" data-testid="preview-zoom-in" aria-label="Yakınlaştır" title="Yakınlaştır"
          onClick={onZoomIn} disabled={busy} className="zen-evrak-btn">
          <ZoomIn className="w-4 h-4" />
        </button>
        <button type="button" data-testid="preview-fit" title={fitLabel}
          onClick={onFit} disabled={busy} className={`zen-evrak-btn ${fitActive ? "is-active" : ""}`}>
          <Maximize2 className="w-4 h-4" /> {fitLabel}
        </button>
      </div>
      <div className="flex-1" />
      <div className="flex items-center gap-2">
        <button type="button" data-testid="preview-download" title="PDF İndir"
          onClick={onDownload} className="zen-evrak-btn">
          <FileDown className="w-4 h-4" /> PDF İndir
        </button>
        <button type="button" data-testid="preview-print" title="Yazdır"
          onClick={onPrint} className="zen-evrak-btn">
          <Printer className="w-4 h-4" /> Yazdır
        </button>
      </div>
    </div>
  );
}

/**
 * jsPDF belgesini pdf.js ile önizler.
 * @param {object} doc        jsPDF örneği
 * @param {function} onDownload  "PDF İndir" eylemi
 * @param {function} onPrint     "Yazdır" eylemi
 */
export const PdfPrintPreview = ({ doc, onDownload, onPrint }) => {
  const [pdf, setPdf] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(true);
  const [zoom, setZoom] = useState(null);   // null → sayfaya sığdır
  const [fitScale, setFitScale] = useState(1);
  const areaRef = useRef(null);
  const canvasRefs = useRef([]);

  // PDF belgesini bir kez yükle (worker blob: üzerinden — MIME'dan bağımsız)
  // Not: paylaşımlı worker'ı yok etmemek için doküman destroy EDİLMEZ (bkz. pdfWorker.js)
  useEffect(() => {
    let alive = true;
    setPdf(null); setError(""); setBusy(true); setZoom(null);
    canvasRefs.current = [];
    loadPdfDocument(doc.output("arraybuffer"))
      .then((v) => {
        if (alive) { setPdf(v); setBusy(false); }
      })
      .catch((e) => { if (alive) { setError(e?.message || "PDF açılamadı"); setBusy(false); } });
    return () => { alive = false; };
  }, [doc]);

  // Sayfaya sığdırma ölçeği — önizleme alanının yüksekliğine/genişliğine göre
  useEffect(() => {
    const area = areaRef.current;
    if (!area || !pdf) return;
    let cancelled = false;
    const computeFit = () => {
      pdf.getPage(1).then((page) => {
        if (cancelled) return;
        const vp = page.getViewport({ scale: 1 });
        const availH = area.clientHeight - 16;
        const availW = area.clientWidth - 16;
        if (availH <= 0 || availW <= 0) return;
        setFitScale(Math.max(MIN_ZOOM, Math.min(availH / vp.height, availW / vp.width)));
      }).catch(() => { /* yoksay */ });
    };
    computeFit();
    const ro = new ResizeObserver(computeFit);
    ro.observe(area);
    return () => { cancelled = true; ro.disconnect(); };
  }, [pdf]);

  const scale = zoom ?? fitScale;

  // Sayfaları seçilen ölçekte çiz (retina netliğinde)
  useEffect(() => {
    if (!pdf) return;
    let cancelled = false;
    const tasks = [];
    (async () => {
      for (let i = 1; i <= pdf.numPages; i++) {
        if (cancelled) return;
        const canvas = canvasRefs.current[i - 1];
        if (!canvas) continue;
        try {
          const page = await pdf.getPage(i);
          if (cancelled) return;
          const dpr = Math.min(2, window.devicePixelRatio || 1);
          const vp = page.getViewport({ scale: scale * dpr });
          canvas.width = Math.floor(vp.width);
          canvas.height = Math.floor(vp.height);
          canvas.style.width = `${Math.floor(vp.width / dpr)}px`;
          canvas.style.height = `${Math.floor(vp.height / dpr)}px`;
          const task = page.render({ canvasContext: canvas.getContext("2d"), viewport: vp });
          tasks.push(task);
          await task.promise;
        } catch (e) {
          if (e?.name !== "RenderingCancelledException") { /* sonraki sayfaya devam */ }
        }
      }
    })();
    return () => {
      cancelled = true;
      tasks.forEach((t) => { try { t.cancel(); } catch (_) { /* yoksay */ } });
    };
  }, [pdf, scale]);

  const zoomIn = () => setZoom(Math.min(MAX_ZOOM, (zoom ?? fitScale) * ZOOM_STEP));
  const zoomOut = () => setZoom(Math.max(MIN_ZOOM, (zoom ?? fitScale) / ZOOM_STEP));
  const zoomFit = () => setZoom(null);

  return (
    <div className="flex flex-col min-h-0 flex-1" data-testid="pdf-print-preview">
      <PreviewToolbar
        zoomPct={Math.round(scale * 100)}
        fitActive={zoom === null}
        busy={busy || !pdf}
        onZoomIn={zoomIn}
        onZoomOut={zoomOut}
        onFit={zoomFit}
        onDownload={onDownload}
        onPrint={onPrint}
      />
      <div ref={areaRef} data-testid="preview-pages"
        className="mt-2 flex flex-col flex-1 min-h-[420px] overflow-auto rounded-xl bg-black/10 dark:bg-black/20 p-2">
        {error ? (
          <div role="alert" data-testid="preview-pdf-error"
            className="p-4 text-sm text-red-700 bg-red-50 dark:bg-red-950/40 dark:text-red-200 rounded-lg break-words">
            Önizleme açılamadı: {error}
          </div>
        ) : pdf ? (
          /* m-auto: alan büyükse sayfa ortalanır, küçükse kırpılmadan kaydırılır */
          <div className="m-auto flex flex-col items-center gap-4">
            {Array.from({ length: pdf.numPages }, (_, i) => (
              <canvas key={i}
                ref={(el) => { canvasRefs.current[i] = el; }}
                role="img" aria-label={`Belge sayfa ${i + 1}`}
                data-testid={`preview-page-${i + 1}`}
                className="block bg-white shadow-lg" />
            ))}
          </div>
        ) : (
          <div data-testid="preview-loading" className="m-auto h-[360px] flex items-center justify-center gap-2 text-sm text-slate-400">
            <Loader2 className="w-4 h-4 animate-spin" /> Belge hazırlanıyor…
          </div>
        )}
      </div>
    </div>
  );
};
